import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../api.js";
import { Badge, Button, Card, COLORS, Page } from "../ui.js";

interface StripeStatus {
  connected: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  configured: boolean;
  dev?: boolean;
}

export default function StripeConnect() {
  const status = useQuery({
    queryKey: ["stripe-status"],
    queryFn: () => api.get<StripeStatus>("/stripe/connect/status"),
  });

  const onboard = useMutation({
    mutationFn: () =>
      api.post<{ url: string; dev: boolean; configured: boolean }>(
        "/stripe/connect/onboarding-link"
      ),
    onSuccess: (data) => {
      if (data.dev) {
        alert(
          "Stripe Connect n'est pas configuré (mode dev). Ajoutez STRIPE_PLATFORM_SECRET_KEY dans .env pour activer le vrai onboarding."
        );
      } else {
        window.location.href = data.url;
      }
    },
  });

  return (
    <Page title="Paiements (Stripe Connect)">
      <Card>
        <h3 style={{ marginTop: 0 }}>État du compte</h3>

        {!status.data?.configured && (
          <div
            style={{
              padding: 12,
              background: "#fef3c7",
              border: "1px solid #fde68a",
              borderRadius: 6,
              marginBottom: 16,
            }}
          >
            <strong>Stripe non configuré côté plateforme.</strong> Les paiements réels ne sont pas
            encore actifs. Configurez <code>STRIPE_PLATFORM_SECRET_KEY</code> dans le fichier{" "}
            <code>.env</code> de l'API.
          </div>
        )}

        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <tbody>
            <Row label="Compte connecté" value={status.data?.connected} />
            <Row label="Paiements activés" value={status.data?.chargesEnabled} />
            <Row label="Versements activés" value={status.data?.payoutsEnabled} />
            <Row label="Onboarding complet" value={status.data?.detailsSubmitted} />
          </tbody>
        </table>

        <div style={{ marginTop: 16 }}>
          <Button onClick={() => onboard.mutate()} disabled={onboard.isPending}>
            {status.data?.connected ? "Continuer l'onboarding" : "Connecter Stripe"}
          </Button>
        </div>
      </Card>

      <Card>
        <h3 style={{ marginTop: 0 }}>Comment ça marche</h3>
        <ol style={{ color: COLORS.muted, lineHeight: 1.7 }}>
          <li>Stripe Connect Express vous redirige vers son interface d'onboarding (KYC).</li>
          <li>Vous renseignez vos informations (entreprise, IBAN, pièces d'identité).</li>
          <li>
            Une fois validé, les paiements de vos Clients arrivent directement sur votre compte
            Stripe.
          </li>
          <li>
            Alchimie ne touche pas votre argent — la plateforme prend uniquement une commission
            (à définir).
          </li>
        </ol>
      </Card>
    </Page>
  );
}

function Row({ label, value }: { label: string; value: boolean | undefined }) {
  return (
    <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
      <td style={{ padding: "10px 0" }}>{label}</td>
      <td style={{ padding: "10px 0", textAlign: "right" }}>
        {value === undefined ? "—" : value ? <Badge kind="ok">Oui</Badge> : <Badge>Non</Badge>}
      </td>
    </tr>
  );
}
